// Formatting helpers. Percentages keep 2dp because the KPI delta is measured in
// points and 94.5 vs 94.53 changes whether a -3.48 pt drop reconciles.

export function pct(n, dp = 2) {
  return n === null || n === undefined ? '—' : `${Number(n).toFixed(dp)}%`;
}

export function signedPts(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  return `${v > 0 ? '+' : ''}${v.toFixed(2)} pts`;
}

/** MCIP-24 — release chip label. Bare versions get the scope's artifact name;
 *  colon-labeled versions (k2web:…, payments-api:…) already carry theirs —
 *  same rule as the API's releaseLabel, kept in sync by the api tests. */
export function releaseLabel(version, scope) {
  if (!version) return null;
  const v = String(version);
  if (/^(k2web|webapp)/i.test(v) || v.includes(':')) return v;
  return `${scope === 'browser' ? 'webapp' : 'k2web'} ${v}`;
}

export function compact(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
}

export function shortDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function shortTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** HH:MM in UTC — the shared top bar's "data as of" stamp (board rule 2:
 *  no auto-refresh, so the fetch time must be visible and unambiguous). */
export function utcHm(value) {
  if (value === null || value === undefined) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** Human name for a run — nobody parses 11-digit run ids. Date + start time is
 *  unique in practice (nightlies) and still distinguishes same-day re-runs. */
export function runName(run) {
  if (!run) return '—';
  return `${shortDate(run.started_at)} · ${shortTime(run.started_at)}`;
}

/**
 * Java-style FQN → the part a human matches on: `Class.method` (the last two
 * dot-segments). `com.gomotive.…events.DownloadEventVideoZipApiTests.
 * result200WithValidUrl_when_x` differs from its siblings only at the END —
 * package prefixes hide the difference and overflow containers (Aug 11
 * review). Full name belongs in the tooltip, never the row.
 */
export function shortTestName(name) {
  const s = String(name || '');
  const parts = s.split('.').filter(Boolean);
  if (parts.length <= 2) return s;
  return parts.slice(-2).join('.');
}

export function titleCase(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Escape text bound for an ECharts tooltip. ECharts renders tooltip HTML via
 * innerHTML, and every label here (cluster titles, pod names, deploy versions)
 * originates in CI data — so it is untrusted by definition.
 */
export function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
